import React, { useState, useMemo, useCallback } from 'react';
import { nasaExoplanetAPI } from '../services/api';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Tooltip,
  InputAdornment, Divider, ToggleButton,
  ToggleButtonGroup, LinearProgress,
} from '@mui/material';
import {
  Search, Close, FilterList,
  ViewList, GridView, Clear, Science, Explore,
} from '@mui/icons-material';
 
// ─── Exoplanet Data ───────────────────────────────────────────────────────────
const EXOPLANETS = [
  { id: 1,  name: 'Proxima Centauri b', hostStar: 'Proxima Centauri',  distanceLY: 4.24,   mass: 1.07,  radius: 1.1,  orbitalPeriod: 11.2,  discoveryYear: 2016, discoveryMethod: 'Radial Velocity', type: 'Super Earth',  habitableZone: true,  temperature: 234,  constellation: 'Centaurus',  description: 'The closest known exoplanet to Earth, orbiting within the habitable zone of Proxima Centauri. A prime candidate in the search for extraterrestrial life.', starType: 'Red Dwarf',             starMass: 0.12, color: '#ef5350' },
  { id: 2,  name: 'TRAPPIST-1e',        hostStar: 'TRAPPIST-1',        distanceLY: 39.46,  mass: 0.69,  radius: 0.92, orbitalPeriod: 6.1,   discoveryYear: 2017, discoveryMethod: 'Transit',         type: 'Terrestrial',  habitableZone: true,  temperature: 251,  constellation: 'Aquarius',   description: 'One of seven Earth-sized planets in the TRAPPIST-1 system. Considered the most likely to harbor liquid water and potentially life.',                    starType: 'Ultra-cool Dwarf',      starMass: 0.09, color: '#ffffff' },
  { id: 3,  name: 'Kepler-452b',        hostStar: 'Kepler-452',        distanceLY: 1402,   mass: 5.0,   radius: 1.63, orbitalPeriod: 384.8, discoveryYear: 2015, discoveryMethod: 'Transit',         type: 'Super Earth',  habitableZone: true,  temperature: 265,  constellation: 'Cygnus',     description: "Often called Earth's older cousin, this planet orbits a Sun-like star in the habitable zone with a remarkably similar orbital period.",                 starType: 'G-type (Sun-like)',     starMass: 1.04, color: '#ffd600' },
  { id: 4,  name: '51 Pegasi b',        hostStar: '51 Pegasi',         distanceLY: 50.9,   mass: 150.0, radius: 1.9,  orbitalPeriod: 4.23,  discoveryYear: 1995, discoveryMethod: 'Radial Velocity', type: 'Hot Jupiter',  habitableZone: false, temperature: 1265, constellation: 'Pegasus',    description: 'The first exoplanet confirmed orbiting a Sun-like star. This hot Jupiter revolutionized our understanding of planetary systems.',                       starType: 'G-type (Sun-like)',     starMass: 1.11, color: '#ffa726' },
  { id: 5,  name: 'HD 209458 b',        hostStar: 'HD 209458',         distanceLY: 159,    mass: 220.0, radius: 1.38, orbitalPeriod: 3.52,  discoveryYear: 1999, discoveryMethod: 'Transit',         type: 'Hot Jupiter',  habitableZone: false, temperature: 1450, constellation: 'Pegasus',    description: 'Known as "Osiris", this was the first exoplanet observed transiting its star. Its atmosphere has been studied extensively.',                            starType: 'G-type (Sun-like)',     starMass: 1.15, color: '#ffa726' },
  { id: 6,  name: 'GJ 667C c',          hostStar: 'GJ 667C',           distanceLY: 23.6,   mass: 3.8,   radius: 1.5,  orbitalPeriod: 28.1,  discoveryYear: 2011, discoveryMethod: 'Radial Velocity', type: 'Super Earth',  habitableZone: true,  temperature: 277,  constellation: 'Scorpius',   description: 'A super-Earth in the habitable zone of a red dwarf star in a triple star system.',                                                                       starType: 'Red Dwarf',             starMass: 0.31, color: '#ef5350' },
  { id: 7,  name: 'WASP-17b',           hostStar: 'WASP-17',           distanceLY: 1000,   mass: 159.0, radius: 1.89, orbitalPeriod: 3.73,  discoveryYear: 2009, discoveryMethod: 'Transit',         type: 'Hot Jupiter',  habitableZone: false, temperature: 1755, constellation: 'Scorpius',   description: "One of the largest exoplanets known, with a retrograde orbit — it orbits opposite to its star's rotation.",                                             starType: 'F-type',                starMass: 1.2,  color: '#ab47bc' },
  { id: 8,  name: 'Kepler-186f',        hostStar: 'Kepler-186',        distanceLY: 582,    mass: 1.4,   radius: 1.17, orbitalPeriod: 129.9, discoveryYear: 2014, discoveryMethod: 'Transit',         type: 'Terrestrial',  habitableZone: true,  temperature: 188,  constellation: 'Cygnus',     description: 'The first Earth-sized planet confirmed in the habitable zone of another star.',                                                                          starType: 'Red Dwarf',             starMass: 0.48, color: '#ef5350' },
  { id: 9,  name: 'K2-18b',             hostStar: 'K2-18',             distanceLY: 124,    mass: 8.6,   radius: 2.27, orbitalPeriod: 32.9,  discoveryYear: 2015, discoveryMethod: 'Transit',         type: 'Mini Neptune', habitableZone: true,  temperature: 265,  constellation: 'Leo',        description: 'A sub-Neptune exoplanet with water vapor detected in its atmosphere. Considered a potential "Hycean" world.',                                           starType: 'Red Dwarf',             starMass: 0.36, color: '#ffffff' },
  { id: 10, name: 'TOI 700 d',          hostStar: 'TOI 700',           distanceLY: 101.4,  mass: 1.72,  radius: 1.19, orbitalPeriod: 37.4,  discoveryYear: 2020, discoveryMethod: 'Transit',         type: 'Terrestrial',  habitableZone: true,  temperature: 269,  constellation: 'Dorado',     description: 'An Earth-sized planet in the habitable zone discovered by the TESS mission.',                                                                            starType: 'Red Dwarf',             starMass: 0.42, color: '#ef5350' },
  { id: 11, name: 'LHS 1140 b',         hostStar: 'LHS 1140',          distanceLY: 40.7,   mass: 6.98,  radius: 1.73, orbitalPeriod: 24.7,  discoveryYear: 2017, discoveryMethod: 'Transit',         type: 'Super Earth',  habitableZone: true,  temperature: 230,  constellation: 'Cetus',      description: 'A rocky super-Earth in the habitable zone, considered one of the best candidates for atmospheric study.',                                               starType: 'Red Dwarf',             starMass: 0.15, color: '#ef5350' },
  { id: 12, name: 'TRAPPIST-1d',        hostStar: 'TRAPPIST-1',        distanceLY: 39.46,  mass: 0.30,  radius: 0.77, orbitalPeriod: 4.05,  discoveryYear: 2017, discoveryMethod: 'Transit',         type: 'Terrestrial',  habitableZone: true,  temperature: 288,  constellation: 'Aquarius',   description: 'The innermost potentially habitable planet in the TRAPPIST-1 system, slightly smaller than Earth.',                                                      starType: 'Ultra-cool Dwarf',      starMass: 0.09, color: '#ffffff' },
  { id: 13, name: 'Tau Ceti e',         hostStar: 'Tau Ceti',          distanceLY: 11.9,   mass: 4.3,   radius: 1.6,  orbitalPeriod: 168.1, discoveryYear: 2012, discoveryMethod: 'Radial Velocity', type: 'Super Earth',  habitableZone: true,  temperature: 273,  constellation: 'Cetus',      description: "A super-Earth candidate orbiting one of the Sun's nearest neighbours, Tau Ceti.",                                                                        starType: 'G-type (Sun-like)',     starMass: 0.78, color: '#ffd600' },
  { id: 14, name: 'Kepler-22b',         hostStar: 'Kepler-22',         distanceLY: 620,    mass: 9.1,   radius: 2.4,  orbitalPeriod: 289.9, discoveryYear: 2011, discoveryMethod: 'Transit',         type: 'Mini Neptune', habitableZone: true,  temperature: 262,  constellation: 'Cygnus',     description: 'The first exoplanet found by Kepler to orbit in the habitable zone of a Sun-like star.',                                                                 starType: 'G-type (Sun-like)',     starMass: 0.97, color: '#ffd600' },
  { id: 15, name: 'Gliese 667 Cc',      hostStar: 'Gliese 667C',       distanceLY: 22.7,   mass: 4.5,   radius: 1.5,  orbitalPeriod: 28.1,  discoveryYear: 2011, discoveryMethod: 'Radial Velocity', type: 'Super Earth',  habitableZone: true,  temperature: 277,  constellation: 'Scorpius',   description: 'A potentially habitable super-Earth orbiting a red dwarf in a triple-star system.',                                                                      starType: 'Red Dwarf',             starMass: 0.31, color: '#ef5350' },
  { id: 16, name: 'HD 40307 g',         hostStar: 'HD 40307',          distanceLY: 41.8,   mass: 7.1,   radius: 1.9,  orbitalPeriod: 197.8, discoveryYear: 2012, discoveryMethod: 'Radial Velocity', type: 'Super Earth',  habitableZone: true,  temperature: 271,  constellation: 'Pictor',     description: 'A super-Earth in the outer habitable zone of an orange dwarf star.',                                                                                     starType: 'K-type (Orange Dwarf)', starMass: 0.77, color: '#ffa726' },
  { id: 17, name: 'Ross 128 b',         hostStar: 'Ross 128',          distanceLY: 11.0,   mass: 1.35,  radius: 1.1,  orbitalPeriod: 9.9,   discoveryYear: 2017, discoveryMethod: 'Radial Velocity', type: 'Terrestrial',  habitableZone: true,  temperature: 294,  constellation: 'Virgo',      description: 'The second closest known temperate Earth-mass exoplanet.',                                                                                               starType: 'Red Dwarf',             starMass: 0.17, color: '#ef5350' },
  { id: 18, name: 'Wolf 1061 c',        hostStar: 'Wolf 1061',         distanceLY: 13.8,   mass: 4.3,   radius: 1.6,  orbitalPeriod: 17.9,  discoveryYear: 2015, discoveryMethod: 'Radial Velocity', type: 'Super Earth',  habitableZone: true,  temperature: 271,  constellation: 'Ophiuchus',  description: 'One of the closest potentially habitable exoplanets, orbiting a nearby red dwarf.',                                                                      starType: 'Red Dwarf',             starMass: 0.29, color: '#ef5350' },
  { id: 19, name: 'WASP-12b',           hostStar: 'WASP-12',           distanceLY: 871,    mass: 464.0, radius: 1.79, orbitalPeriod: 1.09,  discoveryYear: 2008, discoveryMethod: 'Transit',         type: 'Hot Jupiter',  habitableZone: false, temperature: 2580, constellation: 'Auriga',     description: 'An extremely hot Jupiter being slowly consumed by its host star. One of the hottest exoplanets known.',                                                  starType: 'G-type (Sun-like)',     starMass: 1.35, color: '#ffa726' },
  { id: 20, name: 'Kepler-16b',         hostStar: 'Kepler-16',         distanceLY: 245,    mass: 105.0, radius: 0.75, orbitalPeriod: 228.8, discoveryYear: 2011, discoveryMethod: 'Transit',         type: 'Gas Giant',    habitableZone: false, temperature: 170,  constellation: 'Cygnus',     description: 'The first confirmed circumbinary planet — it orbits two stars, like Tatooine from Star Wars!',                                                          starType: 'Binary (K+M)',          starMass: 0.69, color: '#ffd600' },
];
 
const PLANET_TYPES      = ['All Types', 'Terrestrial', 'Super Earth', 'Mini Neptune', 'Gas Giant', 'Hot Jupiter'];
const STAR_TYPES        = ['All Stars', 'Red Dwarf', 'G-type (Sun-like)', 'K-type (Orange Dwarf)', 'F-type', 'Ultra-cool Dwarf', 'Binary (K+M)'];
const DISCOVERY_METHODS = ['All Methods', 'Transit', 'Radial Velocity'];
 
const TYPE_COLORS = {
  'Terrestrial':  '#ffffff',
  'Super Earth':  '#66bb6a',
  'Mini Neptune': '#7e57c2',
  'Gas Giant':    '#ffa726',
  'Hot Jupiter':  '#ef5350',
};
 
// ─── Helper ───────────────────────────────────────────────────────────────────
function getTypeFromMass(mass) {
  if (!mass) return 'Unknown';
  const m = parseFloat(mass);
  if (m < 2)   return 'Terrestrial';
  if (m < 10)  return 'Super Earth';
  if (m < 50)  return 'Mini Neptune';
  if (m < 200) return 'Gas Giant';
  return 'Hot Jupiter';
}
 
// ─── Planet Visualizer ────────────────────────────────────────────────────────
function PlanetVisualizer({ planet }) {
  const size  = Math.min(80, Math.max(20, (planet.radius || 1) * 30));
  const color = TYPE_COLORS[planet.type] || '#ffffff';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
      <Box sx={{
        width: 40, height: 40, borderRadius: '50%', flexShrink: 0, mr: 2,
        background: `radial-gradient(circle at 35% 35%, white, ${planet.color || '#ffd600'})`,
        boxShadow: `0 0 20px ${planet.color || '#ffd600'}88`,
      }} />
      <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(255,255,255,0.1)', position: 'relative', mx: 1 }}>
        <Box sx={{
          position: 'absolute', right: 0, top: '50%',
          transform: 'translate(50%, -50%)',
          width: size, height: size, borderRadius: '50%', zIndex: 2,
          background: `radial-gradient(circle at 35% 35%, ${color}cc, ${color})`,
          boxShadow: `0 0 ${size / 2}px ${color}66`,
        }} />
      </Box>
    </Box>
  );
}
 
// ─── Planet Card ──────────────────────────────────────────────────────────────
function PlanetCard({ planet, onClick }) {
  const typeColor = TYPE_COLORS[planet.type] || '#ffffff';
  return (
    <Card onClick={onClick} sx={{
      cursor: 'pointer', height: '100%',
      border: `1px solid ${typeColor}33`, transition: 'all 0.2s',
      position: 'relative', overflow: 'hidden',
      '&:hover': { border: `1px solid ${typeColor}88`, transform: 'translateY(-3px)', boxShadow: `0 8px 24px ${typeColor}22` },
      '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: typeColor },
    }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{
            width: Math.min(48, Math.max(24, (planet.radius || 1) * 18)),
            height: Math.min(48, Math.max(24, (planet.radius || 1) * 18)),
            borderRadius: '50%', flexShrink: 0,
            background: `radial-gradient(circle at 35% 35%, ${typeColor}cc, ${typeColor})`,
            boxShadow: `0 0 12px ${typeColor}66`,
          }} />
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }} noWrap>
              {planet.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              ⭐ {planet.hostStar}
            </Typography>
          </Box>
          {planet.habitableZone && (
            <Tooltip title="In Habitable Zone">
              <Typography sx={{ fontSize: 16 }}>💧</Typography>
            </Tooltip>
          )}
        </Box>
 
        <Chip label={planet.type} size="small"
          sx={{ bgcolor: `${typeColor}22`, color: typeColor, border: `1px solid ${typeColor}44`,
            fontWeight: 700, fontSize: 10, mb: 1.5 }} />
 
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8 }}>
          {[
            { label: 'Distance', value: `${planet.distanceLY} ly` },
            { label: 'Mass',     value: `${planet.mass}× Earth`   },
            { label: 'Radius',   value: `${planet.radius}× Earth` },
            { label: 'Period',   value: `${planet.orbitalPeriod}d` },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1, p: 0.8 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: 9 }}>{label}</Typography>
              <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700, fontSize: 11 }}>{value}</Typography>
            </Box>
          ))}
        </Box>
 
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
            {planet.discoveryMethod} • {planet.discoveryYear}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
            {planet.constellation}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
 
// ─── Planet Detail Dialog ─────────────────────────────────────────────────────
function PlanetDetailDialog({ planet, onClose }) {
  if (!planet) return null;
  const typeColor = TYPE_COLORS[planet.type] || '#ffffff';
  return (
    <Dialog open={!!planet} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper', border: `1px solid ${typeColor}44` } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
            background: `radial-gradient(circle at 35% 35%, ${typeColor}cc, ${typeColor})`,
            boxShadow: `0 0 20px ${typeColor}66`,
          }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: typeColor }}>{planet.name}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Chip label={planet.type} size="small"
                sx={{ bgcolor: `${typeColor}22`, color: typeColor, fontWeight: 700, fontSize: 10 }} />
              {planet.habitableZone && (
                <Chip label="💧 Habitable Zone" size="small"
                  sx={{ bgcolor: 'rgba(102,187,106,0.2)', color: '#66bb6a', fontWeight: 700, fontSize: 10 }} />
              )}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}><Close /></IconButton>
      </DialogTitle>
 
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
 
      <DialogContent sx={{ pt: 2 }}>
        <PlanetVisualizer planet={planet} />
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.9, mb: 3 }}>
          {planet.description}
        </Typography>
 
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.light', mb: 1.5,
              textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
              Physical Properties
            </Typography>
            {[
              { label: 'Mass',   value: `${planet.mass} Earth masses`,  pct: Math.min(100, ((planet.mass || 0) / 15) * 100),  color: typeColor },
              { label: 'Radius', value: `${planet.radius} Earth radii`, pct: Math.min(100, ((planet.radius || 0) / 3) * 100), color: typeColor },
              { label: 'Avg Temperature',
                value: planet.temperature ? `${planet.temperature} K (${Math.round(planet.temperature - 273)}°C)` : 'Unknown',
                pct:   planet.temperature ? Math.min(100, (planet.temperature / 1500) * 100) : 0,
                color: (planet.temperature || 0) > 500 ? '#ef5350' : '#66bb6a' },
            ].map(({ label, value, pct, color }) => (
              <Box key={label} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>{value}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct || 0}
                  sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)',
                    '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }} />
              </Box>
            ))}
          </Grid>
 
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'primary.light', mb: 1.5,
              textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
              Orbital & Discovery
            </Typography>
            {[
              { label: 'Host Star',        value: planet.hostStar },
              { label: 'Star Type',        value: planet.starType },
              { label: 'Star Mass',        value: `${planet.starMass} Solar masses` },
              { label: 'Orbital Period',   value: `${planet.orbitalPeriod} days` },
              { label: 'Distance',         value: `${planet.distanceLY} light-years` },
              { label: 'Constellation',    value: planet.constellation },
              { label: 'Discovery Year',   value: planet.discoveryYear },
              { label: 'Discovery Method', value: planet.discoveryMethod },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between',
                py: 0.8, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{label}</Typography>
                <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 700 }}>{value}</Typography>
              </Box>
            ))}
          </Grid>
        </Grid>
 
        <Box sx={{ mt: 3, p: 2, borderRadius: 2,
          bgcolor: planet.habitableZone ? 'rgba(102,187,106,0.08)' : 'rgba(239,83,80,0.08)',
          border: planet.habitableZone ? '1px solid rgba(102,187,106,0.3)' : '1px solid rgba(239,83,80,0.2)' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800,
            color: planet.habitableZone ? '#66bb6a' : '#ef5350', mb: 1 }}>
            {planet.habitableZone ? '✅ Potentially Habitable' : '❌ Outside Habitable Zone'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
            {planet.habitableZone
              ? `${planet.name} lies within its star's habitable zone where liquid water could exist on the surface.`
              : `${planet.name} orbits outside the habitable zone, making liquid water on its surface unlikely.`
            }
          </Typography>
        </Box>
      </DialogContent>
 
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Close</Button>
        <Button variant="outlined"
          href={`https://exoplanetarchive.ipac.caltech.edu/overview/${encodeURIComponent(planet.name)}`}
          target="_blank" rel="noopener noreferrer"
          sx={{ borderColor: 'rgba(255,255,255,0.4)', color: 'primary.light' }}>
          View on NASA Archive ↗
        </Button>
      </DialogActions>
    </Dialog>
  );
}
 
// ─── Main ExoplanetPage ───────────────────────────────────────────────────────
export default function ExoplanetPage() {
  const [search, setSearch]           = useState('');
  const [typeFilter, setTypeFilter]   = useState('All Types');
  const [starFilter, setStarFilter]   = useState('All Stars');
  const [methodFilter, setMethod]     = useState('All Methods');
  const [habFilter, setHabFilter]     = useState(false);
  const [sortBy, setSortBy]           = useState('distance');
  const [viewMode, setViewMode]       = useState('grid');
  const [selected, setSelected]       = useState(null);
  const [showFilters, setShowFilters] = useState(false);
 
  // NASA Live API state
  const [nasaData, setNasaData]       = useState([]);
  const [nasaLoading, setNasaLoading] = useState(false);
  const [nasaError, setNasaError]     = useState('');
  const [dataSource, setDataSource]   = useState('local');
 
  const fetchNASAData = useCallback(async () => {
    setNasaLoading(true);
    setNasaError('');
    try {
      const res = await nasaExoplanetAPI.getExoplanets();
      if (res.data.success && res.data.data) {
        const parsed    = JSON.parse(res.data.data);
        const formatted = parsed.map((p, i) => ({
          id:              1000 + i,
          name:            p.pl_name        || 'Unknown',
          hostStar:        p.hostname       || 'Unknown',
          distanceLY:      p.sy_dist        ? parseFloat(p.sy_dist).toFixed(2)   : '?',
          mass:            p.pl_masse       ? parseFloat(p.pl_masse).toFixed(2)  : '?',
          radius:          p.pl_rade        ? parseFloat(p.pl_rade).toFixed(2)   : '?',
          orbitalPeriod:   p.pl_orbper      ? parseFloat(p.pl_orbper).toFixed(1) : '?',
          discoveryYear:   p.disc_year      || '?',
          discoveryMethod: p.discoverymethod || 'Unknown',
          temperature:     p.pl_eqt         ? parseFloat(p.pl_eqt) : null,
          starType:        p.st_spectype    || 'Unknown',
          starMass:        p.st_mass        ? parseFloat(p.st_mass).toFixed(2) : '?',
          constellation:   'See NASA Archive',
          habitableZone:   p.pl_eqt ? (p.pl_eqt >= 180 && p.pl_eqt <= 320) : false,
          type:            getTypeFromMass(p.pl_masse),
          color:           '#ffffff',
          description:     `Discovered in ${p.disc_year} using ${p.discoverymethod}. Orbits ${p.hostname}.`,
        }));
        setNasaData(formatted);
        setDataSource('nasa');
      }
    } catch (err) {
      setNasaError('NASA API unavailable. Showing local database.');
      setDataSource('local');
    } finally {
      setNasaLoading(false);
    }
  }, []);
 
  const activeData = dataSource === 'nasa' && nasaData.length > 0 ? nasaData : EXOPLANETS;
 
  const filtered = useMemo(() => {
    let result = [...activeData];
    if (search) {
      const q = search.toLowerCase();
      result  = result.filter(p =>
        (p.name          || '').toLowerCase().includes(q) ||
        (p.hostStar      || '').toLowerCase().includes(q) ||
        (p.constellation || '').toLowerCase().includes(q)
      );
    }
    if (typeFilter   !== 'All Types')   result = result.filter(p => p.type             === typeFilter);
    if (starFilter   !== 'All Stars')   result = result.filter(p => p.starType          === starFilter);
    if (methodFilter !== 'All Methods') result = result.filter(p => p.discoveryMethod   === methodFilter);
    if (habFilter)                      result = result.filter(p => p.habitableZone);
 
    result.sort((a, b) => {
      switch (sortBy) {
        case 'distance': return (parseFloat(a.distanceLY)   || 0) - (parseFloat(b.distanceLY)   || 0);
        case 'mass':     return (parseFloat(a.mass)         || 0) - (parseFloat(b.mass)         || 0);
        case 'radius':   return (parseFloat(a.radius)       || 0) - (parseFloat(b.radius)       || 0);
        case 'year':     return (parseFloat(b.discoveryYear)|| 0) - (parseFloat(a.discoveryYear)|| 0);
        case 'name':     return (a.name || '').localeCompare(b.name || '');
        case 'temp':     return (parseFloat(a.temperature)  || 0) - (parseFloat(b.temperature)  || 0);
        default:         return 0;
      }
    });
    return result;
  }, [activeData, search, typeFilter, starFilter, methodFilter, habFilter, sortBy]);
 
  const habitableCount = activeData.filter(p => p.habitableZone).length;
  const activeFilters  = [
    typeFilter !== 'All Types', starFilter !== 'All Stars',
    methodFilter !== 'All Methods', habFilter,
  ].filter(Boolean).length;
 
  const clearFilters = () => {
    setTypeFilter('All Types'); setStarFilter('All Stars');
    setMethod('All Methods');   setHabFilter(false); setSearch('');
  };
 
  return (
    <Box>
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>🪐 Exoplanet Explorer</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {activeData.length} confirmed exoplanets • {habitableCount} in habitable zone • NASA data
          </Typography>
        </Box>
        <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small">
          <ToggleButton value="grid" sx={{ color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } }}>
            <GridView fontSize="small" />
          </ToggleButton>
          <ToggleButton value="list" sx={{ color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } }}>
            <ViewList fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
 
      {/* ── Data Source Toggle ───────────────────────────────────────────────── */}
      <Card sx={{ mb: 2, border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(2,136,209,0.05)' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Science sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.light' }}>
                Data Source:
              </Typography>
            </Box>
            <Chip
              label="📚 Local Database (20 planets)"
              onClick={() => setDataSource('local')}
              sx={{
                cursor: 'pointer', fontWeight: 700,
                bgcolor: dataSource === 'local' ? 'rgba(255,255,255,0.2)'  : 'transparent',
                color:   dataSource === 'local' ? 'primary.light'         : 'text.secondary',
                border:  dataSource === 'local' ? '1px solid rgba(255,255,255,0.6)' : '1px solid rgba(255,255,255,0.1)',
              }}
            />
            <Chip
              label={nasaLoading ? '⏳ Loading NASA...' : '🚀 NASA Live API (100 planets)'}
              onClick={fetchNASAData}
              sx={{
                cursor: 'pointer', fontWeight: 700,
                bgcolor: dataSource === 'nasa' ? 'rgba(255,167,38,0.2)' : 'transparent',
                color:   dataSource === 'nasa' ? '#ffa726'               : 'text.secondary',
                border:  dataSource === 'nasa' ? '1px solid #ffa726'    : '1px solid rgba(255,255,255,0.1)',
              }}
            />
            {nasaError && (
              <Typography variant="caption" sx={{ color: 'warning.main' }}>⚠️ {nasaError}</Typography>
            )}
            {dataSource === 'nasa' && nasaData.length > 0 && (
              <Chip label={`✅ ${nasaData.length} planets loaded from NASA`} size="small"
                sx={{ bgcolor: 'rgba(102,187,106,0.15)', color: '#66bb6a', fontWeight: 700 }} />
            )}
          </Box>
        </CardContent>
      </Card>
 
      {/* ── Stats Bar ───────────────────────────────────────────────────────── */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {[
          { label: 'Total Exoplanets',  value: activeData.length,    color: '#ffffff', icon: '🪐' },
          { label: 'Habitable Zone',    value: habitableCount,        color: '#66bb6a', icon: '💧' },
          { label: 'Closest (ly)',      value: Math.min(...activeData.map(p => parseFloat(p.distanceLY) || 9999)).toFixed(2), color: '#ffa726', icon: '📡' },
          { label: 'Discovery Methods', value: 2,                     color: '#ab47bc', icon: '🔭' },
        ].map(({ label, value, color, icon }) => (
          <Grid item xs={6} sm={3} key={label}>
            <Card sx={{ border: `1px solid ${color}33`, textAlign: 'center' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography sx={{ fontSize: 22, mb: 0.5 }}>{icon}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color }}>{value}</Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
 
      {/* ── Search & Filter Bar ─────────────────────────────────────────────── */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search by name, star, constellation..."
              value={search} onChange={e => setSearch(e.target.value)}
              size="small" sx={{ flex: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <Clear sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} label="Sort By" onChange={e => setSortBy(e.target.value)}>
                <MenuItem value="distance">Distance</MenuItem>
                <MenuItem value="mass">Mass</MenuItem>
                <MenuItem value="radius">Radius</MenuItem>
                <MenuItem value="year">Discovery Year</MenuItem>
                <MenuItem value="name">Name (A-Z)</MenuItem>
                <MenuItem value="temp">Temperature</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterList />}
              onClick={() => setShowFilters(s => !s)}
              sx={{ borderColor: 'rgba(255,255,255,0.4)', color: showFilters ? '#fff' : 'primary.light', whiteSpace: 'nowrap' }}
            >
              Filters {activeFilters > 0 && `(${activeFilters})`}
            </Button>
            {activeFilters > 0 && (
              <Button size="small" onClick={clearFilters} startIcon={<Clear />} sx={{ color: 'text.secondary' }}>
                Clear
              </Button>
            )}
          </Box>
 
          {showFilters && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Planet Type</InputLabel>
                    <Select value={typeFilter} label="Planet Type" onChange={e => setTypeFilter(e.target.value)}>
                      {PLANET_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Star Type</InputLabel>
                    <Select value={starFilter} label="Star Type" onChange={e => setStarFilter(e.target.value)}>
                      {STAR_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Discovery Method</InputLabel>
                    <Select value={methodFilter} label="Discovery Method" onChange={e => setMethod(e.target.value)}>
                      {DISCOVERY_METHODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Chip
                    label="💧 Habitable Zone Only"
                    onClick={() => setHabFilter(h => !h)}
                    sx={{
                      cursor: 'pointer', fontWeight: 700,
                      bgcolor: habFilter ? 'rgba(102,187,106,0.2)' : 'transparent',
                      color:   habFilter ? '#66bb6a'                : 'text.secondary',
                      border:  habFilter ? '1px solid #66bb6a'     : '1px solid rgba(255,255,255,0.15)',
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
 
      {/* Active filter chips */}
      {activeFilters > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {typeFilter   !== 'All Types'   && <Chip size="small" label={`Type: ${typeFilter}`}     onDelete={() => setTypeFilter('All Types')}  sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light' }} variant="outlined" />}
          {starFilter   !== 'All Stars'   && <Chip size="small" label={`Star: ${starFilter}`}     onDelete={() => setStarFilter('All Stars')}  sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light' }} variant="outlined" />}
          {methodFilter !== 'All Methods' && <Chip size="small" label={`Method: ${methodFilter}`} onDelete={() => setMethod('All Methods')}    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light' }} variant="outlined" />}
          {habFilter                      && <Chip size="small" label="Habitable Zone"            onDelete={() => setHabFilter(false)}         sx={{ bgcolor: 'rgba(102,187,106,0.1)', color: '#66bb6a' }} variant="outlined" />}
        </Box>
      )}
 
      {/* Results count */}
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        Showing <strong style={{ color: '#ffffff' }}>{filtered.length}</strong> of {activeData.length} exoplanets
        {dataSource === 'nasa' && <span style={{ color: '#ffa726', marginLeft: 8 }}>• NASA Live Data</span>}
        {search && ` matching "${search}"`}
      </Typography>
 
      {/* ── Grid View ───────────────────────────────────────────────────────── */}
      {viewMode === 'grid' && (
        <Grid container spacing={2}>
          {filtered.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <Explore sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
                <Typography variant="h6">No exoplanets found</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>Try adjusting your search or filters</Typography>
                <Button onClick={clearFilters} sx={{ mt: 2, color: 'primary.main' }}>Clear all filters</Button>
              </Box>
            </Grid>
          ) : filtered.map(planet => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={planet.id}>
              <PlanetCard planet={planet} onClick={() => setSelected(planet)} />
            </Grid>
          ))}
        </Grid>
      )}
 
      {/* ── List View ───────────────────────────────────────────────────────── */}
      {viewMode === 'list' && (
        <TableContainer component={Paper} sx={{ bgcolor: 'background.paper',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Planet', 'Type', 'Host Star', 'Distance (ly)', 'Mass', 'Radius', 'Period (d)', 'Temp (K)', 'HZ', 'Year'].map(h => (
                  <TableCell key={h} sx={{ color: 'text.secondary', fontWeight: 700, fontSize: 10,
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    borderBottom: '1px solid rgba(255,255,255,0.1)', whiteSpace: 'nowrap' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                    No exoplanets found matching your filters
                  </TableCell>
                </TableRow>
              ) : filtered.map(planet => {
                const tc = TYPE_COLORS[planet.type] || '#ffffff';
                return (
                  <TableRow key={planet.id} hover onClick={() => setSelected(planet)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', py: 1.2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: tc, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{planet.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <Chip label={planet.type} size="small"
                        sx={{ bgcolor: `${tc}22`, color: tc, fontSize: 9, fontWeight: 700, height: 18 }} />
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}>{planet.hostStar}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#ffffff', fontWeight: 700 }}>{planet.distanceLY}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary' }}>{planet.mass}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary' }}>{planet.radius}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary' }}>{planet.orbitalPeriod}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)',
                      color: (planet.temperature || 0) > 500 ? '#ef5350' : (planet.temperature || 0) < 200 ? '#81d4fa' : '#66bb6a' }}>
                      {planet.temperature || '—'}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>{planet.habitableZone ? '💧' : '—'}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', color: 'text.secondary', fontSize: 12 }}>{planet.discoveryYear}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
 
      {/* ── Type Legend ─────────────────────────────────────────────────────── */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>Planet Types:</Typography>
        {Object.entries(TYPE_COLORS).map(([type, color]) => (
          <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>{type}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: 12 }}>💧</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }}>Habitable Zone</Typography>
        </Box>
      </Box>
 
      {/* Detail Dialog */}
      <PlanetDetailDialog planet={selected} onClose={() => setSelected(null)} />
    </Box>
  );
}